import { useModeAwareQuery } from "./useModeAwareQuery";
import { useApiConfig } from "@/providers/ApiConfigProvider";
import { graphqlQuery } from "@/lib/api/graphql";
import type { CollectionCase } from "@/types/collections";
import { getGlobalDataStore } from "@/lib/mock-data/global-data-store";

// --- Params ---

export interface GetCollectionCasesParams extends Record<string, string | number | boolean | undefined> {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  priority?: string;
  assignedToUserId?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

// --- Pagination ---

export interface PaginationResult {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

// --- GraphQL types ---

interface CollectionCaseGraphQLNode {
  caseId: number;
  caseNumber: string | null;
  customerId: number;
  customerName: string | null;
  customerAccountNumber: string | null;
  customerEmail: string | null;
  customerPhone: string | null;
  priority: string | null;
  status: string | null;
  strategyId: number | null;
  strategyName: string | null;
  amount: number;
  daysInCollection: number;
  nextAction: string | null;
  nextActionDate: string | null;
  isPaused: boolean;
  assignedToUserId: number | null;
  assignedToName: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  activityCount: number;
  lastActionType: string | null;
  lastActionDescription: string | null;
  lastActionActorName: string | null;
  lastActionDate: string | null;
}

interface GraphQLConnection<T> {
  edges: Array<{ node: T }>;
  pageInfo: {
    startCursor: string | null;
    endCursor: string | null;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

interface CollectionCaseListGraphQLResponse {
  collectionCases: {
    collectionCaseList: GraphQLConnection<CollectionCaseGraphQLNode>;
  };
}

interface CollectionCaseCountGraphQLResponse {
  collectionCases: {
    collectionCaseCount: number;
  };
}

interface CollectionCaseTotalsGraphQLResponse {
  collectionCases: {
    collectionCaseTotals: {
      totalCount: number;
      promisedCount: number;
      highPriorityCount: number;
      dueTodayCount: number;
      totalOutstanding: number;
    };
  };
}

// --- GraphQL queries ---

const COLLECTION_CASE_LIST_QUERY = `
  query CollectionCaseList($first: Int, $after: String, $sort: [CollectionCaseListSortSortByInput!], $filter: CollectionCaseListFilterInput) {
    collectionCases {
      collectionCaseList(first: $first, after: $after, sort: $sort, filter: $filter) {
        edges {
          node {
            caseId
            caseNumber
            customerId
            customerName
            customerAccountNumber
            customerEmail
            customerPhone
            priority
            status
            strategyId
            strategyName
            amount
            daysInCollection
            nextAction
            nextActionDate
            isPaused
            assignedToUserId
            assignedToName
            notes
            createdAt
            updatedAt
            activityCount
            lastActionType
            lastActionDescription
            lastActionActorName
            lastActionDate
          }
        }
        pageInfo {
          startCursor
          endCursor
          hasNextPage
          hasPreviousPage
        }
      }
    }
  }
`;

const COLLECTION_CASE_COUNT_QUERY = `
  query CollectionCaseCount($filter: CollectionCaseListFilterInput) {
    collectionCases {
      collectionCaseCount(filter: $filter)
    }
  }
`;

const COLLECTION_CASE_TOTALS_QUERY = `
  query CollectionCaseTotals($filter: CollectionCaseListFilterInput) {
    collectionCases {
      collectionCaseTotals(filter: $filter) {
        totalCount
        promisedCount
        highPriorityCount
        dueTodayCount
        totalOutstanding
      }
    }
  }
`;

// --- Mapping ---

function mapGraphQLNodeToCollectionCase(node: CollectionCaseGraphQLNode): CollectionCase {
  const lastAction = node.lastActionType
    ? {
        type: node.lastActionType,
        description: node.lastActionDescription || "",
        date: node.lastActionDate ? new Date(node.lastActionDate) : new Date(),
        performedBy: node.lastActionActorName || "Unknown",
      }
    : undefined;

  return {
    id: String(node.caseId),
    caseId: node.caseNumber || String(node.caseId),
    customerId: String(node.customerId),
    customerName: node.customerName || "",
    customerEmail: node.customerEmail || "",
    customerPhone: node.customerPhone || "",
    priority: (node.priority as CollectionCase["priority"]) || "Medium",
    status: (node.status as CollectionCase["status"]) || "Active",
    strategy: {
      id: node.strategyId ? String(node.strategyId) : "",
      name: node.strategyName || "Default",
      type: "Standard",
    },
    strategyPhase: {
      currentPhase: 1,
      totalPhases: 3,
      phaseName: "Initial Contact",
    },
    amount: node.amount,
    daysInCollection: node.daysInCollection,
    nextAction: node.nextAction || "No action scheduled",
    nextActionDate: node.nextActionDate ? new Date(node.nextActionDate) : new Date(),
    createdAt: new Date(node.createdAt),
    updatedAt: new Date(node.updatedAt),
    isPaused: node.isPaused,
    assignedTo: node.assignedToName || undefined,
    notes: node.notes || undefined,
    lastAction,
  };
}

// --- Sort / Filter builders ---

const sortFieldMap: Record<string, string> = {
  priority: "Priority",
  caseId: "CaseNumber",
  customerName: "CustomerName",
  amount: "Amount",
  daysInCollection: "DaysInCollection",
  nextAction: "NextAction",
  nextActionDate: "NextActionDate",
  status: "Status",
  assignedTo: "AssignedToName",
  createdAt: "CreatedAt",
  updatedAt: "UpdatedAt",
};

function buildGraphQLSortVariable(sortBy?: string, sortOrder?: "asc" | "desc") {
  if (!sortBy) return undefined;
  const field = sortFieldMap[sortBy] || sortBy;
  return [{ field, order: sortOrder === "desc" ? "Descending" : "Ascending" }];
}

function buildGraphQLFilterVariable(params: GetCollectionCasesParams) {
  const filter: Record<string, unknown> = {};

  if (params.search) {
    filter.summary = params.search;
  }
  if (params.status && params.status !== "all") {
    filter.status = { eq: params.status };
  }
  if (params.priority && params.priority !== "all") {
    filter.priority = { eq: params.priority };
  }
  if (params.assignedToUserId !== undefined) {
    filter.assignedToUserId = { eq: params.assignedToUserId };
  }

  return Object.keys(filter).length > 0 ? filter : undefined;
}

// --- Cursor helpers ---

function pageToCursor(page: number): string {
  const pageIndex = page - 1;
  const buffer = new ArrayBuffer(4);
  const view = new DataView(buffer);
  view.setInt32(0, pageIndex, true); // little-endian to match C# BitConverter
  const bytes = new Uint8Array(buffer);
  return btoa(String.fromCharCode(...bytes));
}

// --- Mock pagination helper ---

function computeMockPagination(cases: CollectionCase[], page: number, pageSize: number): PaginationResult {
  const totalItems = cases.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  return {
    page,
    pageSize,
    totalItems,
    totalPages,
    hasNextPage: page < totalPages,
    hasPreviousPage: page > 1,
  };
}

function filterMockCases(cases: CollectionCase[], params: GetCollectionCasesParams): CollectionCase[] {
  return cases.filter((caseItem) => {
    const matchesSearch = !params.search ||
      caseItem.customerName.toLowerCase().includes(params.search.toLowerCase()) ||
      caseItem.caseId.toLowerCase().includes(params.search.toLowerCase());

    const matchesStatus = !params.status || params.status === "all" || caseItem.status === params.status;
    const matchesPriority = !params.priority || params.priority === "all" || caseItem.priority === params.priority;
    const matchesUser = params.assignedToUserId === undefined || caseItem.assignedTo !== undefined;

    return matchesSearch && matchesStatus && matchesPriority && matchesUser;
  });
}

// --- Result types ---

export interface CollectionCasesListResult {
  cases: CollectionCase[];
  pagination: PaginationResult;
}

// --- Hooks ---

function useCollectionCaseCount(params?: GetCollectionCasesParams) {
  const apiConfig = useApiConfig();
  const filter = buildGraphQLFilterVariable(params ?? {});

  return useModeAwareQuery<number, Error>(
    ["collection-cases-count", filter],
    async ({ signal }) => {
      const variables: Record<string, unknown> = {};
      if (filter) {
        variables.filter = filter;
      }

      const response = await graphqlQuery<CollectionCaseCountGraphQLResponse>(
        apiConfig,
        COLLECTION_CASE_COUNT_QUERY,
        variables,
        signal,
      );

      return response.collectionCases.collectionCaseCount;
    },
    0,
    {
      staleTime: 30_000,
      refetchOnMount: "always",
    }
  );
}

export function useCollectionCasesListGraphQL(params?: GetCollectionCasesParams) {
  const apiConfig = useApiConfig();
  const mockCases = getGlobalDataStore().getAllCases();
  const queryParams: GetCollectionCasesParams = { page: 1, pageSize: 25, sortBy: "nextActionDate", sortOrder: "asc", ...params };
  const mockPage = queryParams.page ?? 1;
  const mockPageSize = queryParams.pageSize ?? 25;
  const filteredMock = filterMockCases(mockCases, queryParams);
  const mockPagination = computeMockPagination(filteredMock, mockPage, mockPageSize);
  const mockPagedCases = filteredMock.slice((mockPage - 1) * mockPageSize, mockPage * mockPageSize);
  const mockResult: CollectionCasesListResult = { cases: mockPagedCases, pagination: mockPagination };

  return useModeAwareQuery<CollectionCasesListResult, Error>(
    ["collection-cases-list", queryParams],
    async ({ signal }) => {
      const page = queryParams.page ?? 1;
      const pageSize = queryParams.pageSize ?? 25;

      const variables: Record<string, unknown> = {
        first: pageSize,
        sort: buildGraphQLSortVariable(queryParams.sortBy, queryParams.sortOrder),
        filter: buildGraphQLFilterVariable(queryParams),
      };

      if (page > 1) {
        variables.after = pageToCursor(page);
      }

      const response = await graphqlQuery<CollectionCaseListGraphQLResponse>(
        apiConfig,
        COLLECTION_CASE_LIST_QUERY,
        variables,
        signal,
      );

      const connection = response.collectionCases.collectionCaseList;
      const cases = connection.edges.map((x) => mapGraphQLNodeToCollectionCase(x.node));

      const pagination: PaginationResult = {
        page,
        pageSize,
        totalItems: cases.length,
        totalPages: 1,
        hasNextPage: connection.pageInfo.hasNextPage,
        hasPreviousPage: connection.pageInfo.hasPreviousPage,
      };

      return { cases, pagination };
    },
    mockResult,
    {
      staleTime: 0,
      refetchOnMount: "always",
    }
  );
}

// --- Totals result type ---

export interface CollectionCaseTotalsResult {
  totalCount: number;
  promisedCount: number;
  highPriorityCount: number;
  dueTodayCount: number;
  totalOutstanding: number;
}

// --- Totals hook ---

export function useCollectionCaseTotals(params?: GetCollectionCasesParams) {
  const apiConfig = useApiConfig();
  const filter = buildGraphQLFilterVariable(params ?? {});

  const mockCases = getGlobalDataStore().getAllCases();
  const filteredMock = filterMockCases(mockCases, params ?? {});
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const mockTotals: CollectionCaseTotalsResult = {
    totalCount: filteredMock.length,
    promisedCount: filteredMock.filter((x) => x.status === "Promised").length,
    highPriorityCount: filteredMock.filter((x) => x.priority === "High").length,
    dueTodayCount: filteredMock.filter((x) => {
      const actionDate = new Date(x.nextActionDate);
      actionDate.setHours(0, 0, 0, 0);
      return actionDate.getTime() === today.getTime();
    }).length,
    totalOutstanding: filteredMock.reduce((sum, x) => sum + x.amount, 0),
  };

  return useModeAwareQuery<CollectionCaseTotalsResult, Error>(
    ["collection-cases-totals", filter],
    async ({ signal }) => {
      const variables: Record<string, unknown> = {};
      if (filter) {
        variables.filter = filter;
      }

      const response = await graphqlQuery<CollectionCaseTotalsGraphQLResponse>(
        apiConfig,
        COLLECTION_CASE_TOTALS_QUERY,
        variables,
        signal,
      );

      return response.collectionCases.collectionCaseTotals;
    },
    mockTotals,
    {
      staleTime: 30_000,
      refetchOnMount: "always",
    }
  );
}

// --- Composite page data hook ---

const defaultPagination: PaginationResult = {
  page: 1,
  pageSize: 25,
  totalItems: 0,
  totalPages: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

export function useCollectionCasesPageData(params?: GetCollectionCasesParams) {
  const listQuery = useCollectionCasesListGraphQL(params);
  const countQuery = useCollectionCaseCount(params);

  const basePagination = listQuery.data?.pagination ?? defaultPagination;
  const totalCount = countQuery.data ?? 0;

  const pagination: PaginationResult = totalCount > 0
    ? {
        ...basePagination,
        totalItems: totalCount,
        totalPages: basePagination.pageSize > 0
          ? Math.ceil(totalCount / basePagination.pageSize)
          : 1,
      }
    : basePagination;

  return {
    cases: listQuery.data?.cases ?? [],
    pagination,
    isLoading: listQuery.isLoading,
    error: listQuery.error,
    refetch: () => {
      listQuery.refetch();
      countQuery.refetch();
    },
  };
}
